import React, { useMemo } from "react";
import { Drawer } from "@design-system/Drawer/Drawer";
import { useDrawerContentMount } from "@design-system/Drawer/useDrawerContentMount";
import { useEntitiesById } from "@hooks/useEntities";
import { LocalAccountingTabProvider } from "../LocalAccountingTabProvider";
import {
    CHART_OF_ACCOUNTS_1099_MAPPING_VIEW_ID,
    CHART_OF_ACCOUNTS_1099_MAPPING_VIEW_FILTERS,
    ChartOfAccountsBuiltInViewId,
} from "./chartOfAccountsBuiltInViews";
import { ChartOfAccountsPage } from "./ChartOfAccounts";

interface ContentProps {
    entityIds: number[];
    initialViewId: ChartOfAccountsBuiltInViewId;
}

const ChartOfAccountsDrawerContent: React.FC<ContentProps> = ({
    entityIds,
    initialViewId,
}) => {
    const entitiesById = useEntitiesById();
    const selectedEntity =
        entityIds.length === 1 ? entitiesById.get(entityIds[0]) : undefined;
    const initialFilters = useMemo(
        () => ({
            entityIds,
            ...(initialViewId === CHART_OF_ACCOUNTS_1099_MAPPING_VIEW_ID
                ? CHART_OF_ACCOUNTS_1099_MAPPING_VIEW_FILTERS
                : {}),
        }),
        [entityIds, initialViewId],
    );
    const embedded = useMemo(() => ({ initialViewId }), [initialViewId]);

    return (
        <LocalAccountingTabProvider
            initialFilters={initialFilters}
            selectedEntity={selectedEntity}
        >
            <ChartOfAccountsPage embedded={embedded} />
        </LocalAccountingTabProvider>
    );
};

interface Props {
    open: boolean;
    onClose: () => void;
    entityIds: number[];
    initialViewId?: ChartOfAccountsBuiltInViewId;
}

/** Chart of Accounts scoped to the given entities, in a drawer. */
export const ChartOfAccountsDrawer: React.FC<Props> = ({
    open,
    onClose,
    entityIds,
    initialViewId = CHART_OF_ACCOUNTS_1099_MAPPING_VIEW_ID,
}) => {
    const { shouldRender, openKey, onAnimationEnd } =
        useDrawerContentMount(open);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            direction="right"
            size="fullScreenWithNav"
            onAnimationEnd={onAnimationEnd}
            headerAddonLeft="Chart of Accounts"
            noPadding
        >
            {shouldRender && (
                <ChartOfAccountsDrawerContent
                    key={openKey}
                    entityIds={entityIds}
                    initialViewId={initialViewId}
                />
            )}
        </Drawer>
    );
};
